import { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { useAuth } from "../lib/AuthContext";
import { toast } from "sonner";
import {
  FileUp, Sparkles, UserCircle, Bookmark, ShieldCheck, Mail, MessageSquare,
  ArrowRight, Zap, Target, Loader2, UploadCloud, CheckCircle2
} from "lucide-react";

const QUICK = [
  { to: "/upload", i: FileUp, t: "Upload Resume", d: "Feed the agent your CV" },
  { to: "/cover-letter", i: Mail, t: "Cover Letter", d: "Five tones, tailored" },
  { to: "/interview", i: MessageSquare, t: "Interview Prep", d: "Practise before you apply" },
  { to: "/tracker", i: Bookmark, t: "Saved", d: "Your shortlist" },
];

export default function Dashboard() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  useEffect(() => {
    api.get("/dashboard").then((r) => setData(r.data)).catch(() => setData({}));
  }, []);

  return (
    <div className="space-y-10" data-testid="dashboard-root">
      <div className="fade-up">
        <div className="chip mb-3"><Sparkles size={12} /> Welcome back{user?.name ? `, ${user.name.split(" ")[0]}` : ""}</div>
        <h1 className="font-display font-black text-4xl sm:text-5xl lg:text-6xl tracking-tighter">
          <span className="text-neon">Your Job Search Agent</span>
        </h1>
        <p className="mt-3 text-slate-400 text-lg max-w-2xl leading-relaxed">
          Find better jobs. Apply smarter. Track everything.
        </p>
      </div>

      <JobAgentHero user={user} />

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 fade-up-delay-2">
        <StatCard label="Resumes" value={data?.resume_count ?? 0} sub={data?.latest_resume?.filename || "Upload to start"} to="/upload" accent="#00E5FF" />
        <StatCard label="ATS Score" value={data?.ats_score ?? "—"} sub="/ 100" to="/ats" accent="#9D00FF" />
        <StatCard label="Applications" value={data?.applications_total ?? 0} sub="Across all stages" to="/tracker" accent="#FFD166" />
        <StatCard label="Searches" value={data?.recent_searches?.length ?? 0} sub="Recent" to="/jobs" accent="#FF007F" />
      </div>

      <div className="fade-up-delay-3">
        <div className="flex items-end justify-between mb-5">
          <div>
            <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-slate-500 mb-2 flex items-center gap-2">
              <Zap size={11} className="text-[#00E5FF]" /> Quick actions
            </div>
            <h2 className="font-display font-bold text-2xl sm:text-3xl">Pick your next move</h2>
          </div>
          <Link to="/job-agent" className="text-sm text-[#80FFEA] font-semibold hover:text-white transition-colors inline-flex items-center gap-1">
            See matches <ArrowRight size={14} />
          </Link>
        </div>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          {QUICK.map((q) => (
            <Link
              key={q.to}
              to={q.to}
              data-testid={`quick-${q.t.toLowerCase().replace(/\s+/g, "-")}`}
              className="glass p-5 hover-lift block group"
            >
              <span className="grid place-items-center w-11 h-11 rounded-2xl mb-3 bg-gradient-to-br from-[rgba(157,0,255,0.2)] to-[rgba(0,229,255,0.2)] border border-white/10 text-[#80FFEA] group-hover:scale-110 transition-transform">
                <q.i size={18} />
              </span>
              <div className="font-display font-bold text-white">{q.t}</div>
              <div className="text-xs text-slate-500 mt-1">{q.d}</div>
            </Link>
          ))}
        </div>
      </div>
    </div>
  );
}

const STAGES_COPY = [
  { max: 30, label: "Profile needs setup", color: "#FF6DAE" },
  { max: 60, label: "Profile partially complete", color: "#FFD166" },
  { max: 85, label: "Good profile", color: "#80FFEA" },
  { max: 99, label: "Almost ready", color: "#00E5FF" },
  { max: 100, label: "Ready for best matches", color: "#9D00FF" },
];
function stageOf(s) { return STAGES_COPY.find((x) => s <= x.max) || STAGES_COPY[0]; }

const PROGRESS_STEPS = ["Uploading resume", "Reading resume", "Building Career Profile", "Calculating profile strength", "Job Agent ready"];

function JobAgentHero({ user }) {
  const isPremium = user?.subscription === "premium" || user?.is_admin;
  const nav = useNavigate();
  const fileRef = useRef(null);
  const [profileState, setProfileState] = useState({ strength: 0, missing: [], loading: true, hasResume: false });
  const [matches, setMatches] = useState(null);
  const [pipeline, setPipeline] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [progressStep, setProgressStep] = useState(0);

  const loadProfile = async () => {
    try {
      const r = await api.get("/career-profile");
      setProfileState({
        strength: r.data?.strength ?? 0,
        missing: r.data?.missing_fields ?? [],
        loading: false,
        hasResume: !!r.data?.profile,
      });
    } catch { setProfileState((p) => ({ ...p, loading: false })); }
  };

  useEffect(() => {
    loadProfile();
    if (isPremium) {
      api.get("/job-agent/matches?limit=3").then((r) => setMatches(r.data)).catch(() => setMatches({ jobs: [] }));
      api.get("/job-agent/saved").then((r) => setPipeline(r.data)).catch(() => setPipeline(null));
    }
  }, [isPremium]);

  const openPicker = () => fileRef.current?.click();

  const onFile = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setUploading(true);
    setProgressStep(0);
    const timers = [
      setTimeout(() => setProgressStep(1), 1200),
      setTimeout(() => setProgressStep(2), 3500),
      setTimeout(() => setProgressStep(3), 6500),
    ];
    try {
      const fd = new FormData();
      fd.append("file", f);
      const r = await api.post("/resumes/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setProgressStep(4);
      const filled = r.data?.profile_filled_fields || [];
      if (filled.length > 0) {
        toast.success(`Career Profile updated from your resume — ${filled.length} field${filled.length === 1 ? "" : "s"} auto-filled.`);
      } else {
        toast.success("Resume uploaded. Please update the missing fields manually.");
      }
      await loadProfile();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Upload failed. Please try again.");
    } finally {
      timers.forEach(clearTimeout);
      setUploading(false);
      setProgressStep(0);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const strength = profileState.strength;
  const stage = stageOf(strength);
  const missing = profileState.missing.slice(0, 5);
  const isEmpty = strength === 0;
  const isComplete = strength >= 100;
  const jobs = matches?.jobs || [];
  const topScore = jobs[0]?.match_score || 0;
  const savedCount = pipeline?.count || 0;

  // Copy variants
  let title, subtitle;
  if (isEmpty) {
    title = "Your Job Search Agent starts with your resume";
    subtitle = "Upload your CV and let Cavilow build your Career Profile, find better matches, and guide your next application step.";
  } else if (!isComplete) {
    title = "Your Job Search Agent is getting ready";
    subtitle = "Complete the missing details to improve your job matches.";
  } else {
    title = "Your Job Search Agent is ready";
    subtitle = "Your latest job matches are ready based on your CV and Career Profile.";
  }

  return (
    <div
      className="fade-up-delay-1 relative overflow-hidden rounded-[2.5rem] neon-pulse"
      data-testid="dashboard-job-agent-hero"
      style={{
        background: "linear-gradient(135deg, rgba(157,0,255,0.15) 0%, rgba(0,229,255,0.08) 50%, rgba(255,0,127,0.08) 100%), #0A0A12",
        border: "1px solid rgba(255,255,255,0.08)",
      }}
    >
      <div className="absolute -right-24 -top-24 w-80 h-80 rounded-full bg-[#9D00FF]/30 blur-3xl" />
      <div className="absolute -left-16 -bottom-16 w-72 h-72 rounded-full bg-[#00E5FF]/25 blur-3xl" />
      <div className="absolute inset-0 opacity-30 grid-bg pointer-events-none" />

      <input ref={fileRef} type="file" accept=".pdf,.doc,.docx" onChange={onFile}
        data-testid="hero-upload-input" className="hidden" />

      <div className="relative p-6 sm:p-10 grid lg:grid-cols-[1.35fr_1fr] gap-8 items-start">
        {/* LEFT */}
        <div>
          <div className="inline-flex items-center gap-2 rounded-full bg-white/5 border border-white/10 backdrop-blur px-3 py-1 text-[10px] font-bold uppercase tracking-[0.2em] text-[#80FFEA]">
            <Sparkles size={11} /> {isPremium ? "Premium · Job Agent" : "Free · Get started"}
          </div>
          <h2 data-testid="hero-title" className="font-display font-black text-3xl sm:text-4xl lg:text-5xl mt-4 tracking-tighter leading-tight text-white">
            {title}
          </h2>
          <p className="mt-3 text-slate-300 max-w-xl leading-relaxed">{subtitle}</p>

          {/* Progress bar row */}
          {!uploading && !profileState.loading && (
            <div className="mt-6 rounded-2xl bg-white/[0.03] border border-white/8 p-4" data-testid="hero-strength">
              <div className="flex items-center justify-between mb-2">
                <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-slate-400">Career Profile Completion</div>
                <div className="text-sm font-bold" style={{ color: stage.color }} data-testid="hero-strength-value">
                  {strength}% · {stage.label}
                </div>
              </div>
              <div className="h-2 rounded-full bg-white/5 overflow-hidden">
                <div className="h-full rounded-full transition-all" style={{ width: `${strength}%`, background: `linear-gradient(90deg, ${stage.color}, #00E5FF)`, boxShadow: `0 0 12px ${stage.color}88` }} />
              </div>

              {isEmpty && profileState.hasResume === false && (
                <div className="mt-3 text-xs text-slate-400">Resume not found. Upload one to build your profile automatically.</div>
              )}

              {!isComplete && missing.length > 0 && (
                <div className="mt-4">
                  <div className="text-[11px] text-slate-300 mb-2">Complete these details to improve your matches:</div>
                  <div className="flex flex-wrap gap-1.5" data-testid="hero-missing-chips">
                    {missing.map((m) => (
                      <span key={m.key} data-testid={`missing-chip-${m.key}`} className="text-[11px] font-semibold rounded-full px-2.5 py-1 bg-[rgba(255,209,102,0.1)] text-[#FFD166] border border-[rgba(255,209,102,0.3)]">
                        {m.label}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {isComplete && (
                <div className="mt-3 text-xs text-[#80FFEA] flex items-center gap-2" data-testid="profile-complete-msg">
                  <CheckCircle2 size={14} />
                  Your Career Profile is complete. Cavilow can now find better matches for you.
                </div>
              )}
            </div>
          )}

          {/* Upload progress states */}
          {uploading && (
            <div className="mt-6 rounded-2xl bg-white/[0.03] border border-white/8 p-4" data-testid="hero-upload-progress">
              <div className="flex items-center gap-2 mb-3">
                <Loader2 size={16} className="animate-spin text-[#00E5FF]" />
                <span className="text-sm font-bold text-white">{PROGRESS_STEPS[progressStep]}</span>
              </div>
              <div className="grid grid-cols-5 gap-1.5">
                {PROGRESS_STEPS.map((_, i) => (
                  <div key={i} className="h-1 rounded-full" style={{ background: i <= progressStep ? "linear-gradient(90deg, #9D00FF, #00E5FF)" : "rgba(255,255,255,0.08)" }} />
                ))}
              </div>
            </div>
          )}

          {/* CTA row */}
          <div className="mt-6 flex items-center gap-3 flex-wrap">
            <button
              data-testid="hero-upload-cta"
              onClick={openPicker}
              disabled={uploading}
              className="btn-neon inline-flex items-center gap-2 disabled:opacity-60"
            >
              <UploadCloud size={16} /> {isEmpty ? "Upload Resume" : "Re-upload Resume"}
            </button>
            <button
              data-testid="hero-complete-missing"
              onClick={() => nav("/career-profile?focus=missing")}
              className="btn-glass inline-flex items-center gap-2"
            >
              <UserCircle size={15} /> {isComplete ? "Update Career Profile" : "Complete Missing Details"}
            </button>
            {isPremium ? (
              <Link
                to="/job-agent"
                data-testid="hero-view-matches"
                className={`inline-flex items-center gap-2 px-4 py-3 text-sm font-bold transition-colors ${isComplete ? "text-white hover:text-[#80FFEA]" : "text-[#80FFEA] hover:text-white"}`}
              >
                View Matches <ArrowRight size={14} />
              </Link>
            ) : (
              <Link
                to="/pricing"
                data-testid="hero-unlock-agent"
                className="inline-flex items-center gap-2 px-4 py-3 text-sm font-bold text-[#80FFEA] hover:text-white"
              >
                Unlock Job Agent <ShieldCheck size={14} />
              </Link>
            )}
          </div>
        </div>

        {/* RIGHT — matches / pipeline */}
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-slate-500">Top matches</div>
            <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-[#80FFEA]">{isPremium ? "Live" : "Preview"}</div>
          </div>
          {isPremium && jobs.slice(0, 3).map((j) => (
            <div key={j.job_id} className="glass p-3 flex items-center justify-between gap-3 hover:border-[#00E5FF]/40 transition-colors">
              <div className="min-w-0">
                <div className="text-sm font-bold text-white truncate">{j.title}</div>
                <div className="text-[11px] text-slate-500 truncate">{j.company} · {j.location}</div>
              </div>
              <MiniRing score={j.match_score || 0} />
            </div>
          ))}
          {(!isPremium || jobs.length === 0) && (
            <div className="glass p-4 text-sm text-slate-400 leading-relaxed">
              {isComplete
                ? "No live matches yet — check back soon."
                : "Complete your Career Profile to improve your matches."}
            </div>
          )}
          <div className="glass p-3 flex items-center justify-between">
            <div>
              <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-slate-500">Pipeline</div>
              <div className="text-sm font-bold text-white mt-0.5">{savedCount} saved role{savedCount === 1 ? "" : "s"}</div>
            </div>
            <Link to="/pipeline" className="text-[11px] font-bold text-[#80FFEA] hover:text-white inline-flex items-center gap-1">
              Open <ArrowRight size={12} />
            </Link>
          </div>
          {isPremium && topScore > 0 && (
            <div className="glass p-3 flex items-start gap-2 text-xs text-slate-300 leading-relaxed">
              <Target size={14} className="text-[#FFD166] shrink-0 mt-0.5" />
              <span>Your matches are ranked using your CV, skills, experience, target roles, and location preference.</span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function MiniRing({ score }) {
  const c = score >= 80 ? "#00E5FF" : score >= 60 ? "#9D00FF" : "#FF007F";
  const dash = (score / 100) * 100;
  return (
    <div className="relative w-11 h-11 shrink-0">
      <svg viewBox="0 0 36 36" className="w-full h-full -rotate-90">
        <circle cx="18" cy="18" r="15.9" fill="none" className="ring-track" strokeWidth="3" />
        <circle cx="18" cy="18" r="15.9" fill="none" stroke={c} strokeWidth="3" strokeLinecap="round"
          strokeDasharray={`${dash}, 100`} style={{ filter: `drop-shadow(0 0 5px ${c}88)` }} />
      </svg>
      <div className="absolute inset-0 grid place-items-center font-display font-black text-[11px]" style={{ color: c }}>{score}</div>
    </div>
  );
}

function StatCard({ label, value, sub, to, accent }) {
  return (
    <Link to={to || "#"} className="glass p-5 hover-lift block relative overflow-hidden">
      <div className="absolute -top-8 -right-8 w-24 h-24 rounded-full opacity-30 blur-2xl" style={{ background: accent }} />
      <div className="relative flex items-center justify-between">
        <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-slate-500">{label}</div>
        <span className="w-1.5 h-1.5 rounded-full shadow-[0_0_8px_currentColor]" style={{ background: accent, color: accent }} />
      </div>
      <div className="relative mt-2 font-display font-black text-4xl text-white leading-none">{value}</div>
      <div className="relative mt-3 text-xs text-slate-500 truncate">{sub}</div>
    </Link>
  );
}
